import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { updateLastActivity } from '../../Utils/LastActivity';
import { appBarHeight, navBarHeight } from '../../Constants';
import type { IndexedDBContextProps } from '../../Contexts/IndexedDBContext';
import IndexedDBContext from '../../Contexts/IndexedDBContext';
import type { SystemContextProps } from '../../Contexts/SystemContext';
import SystemContext from '../../Contexts/SystemContext';
import CommentItem from '../../Models/CommentItemModel';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import type { ApiContextProps } from '../../Contexts/ApiContext';
import ApiContext from '../../Contexts/ApiContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTitle, handleTab } from 'shouldve-been-react';
import { QuickTextInput, ErrorSnackbar, LoadDataBackdrop, ConfirmDialog } from 'shouldve-been-mui';
import type Folder from '../../Models/FolderModel';

type CommentItemSubTabProps = {
    commentItemId: string,
    closeSubTab: (successMessage: string | null) => void,
};

function CommentItemSubTab(props: Readonly<CommentItemSubTabProps>): JSX.Element {

    useTitle('K-Fleet Experience - Comment Item');

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);
    const systemContext: SystemContextProps = useContext<SystemContextProps>(SystemContext);
    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);

    const currentTheme: Theme = useTheme();

    const inputRef: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);
    const [errorSnackbarMessage, setErrorSnackbarMessage] = useState<string | null>(null);
    const [dataIsLoaded, setDataIsLoaded] = useState<boolean>(false);
    const [latestKeyEvent, setLatestKeyEvent] = useState<React.KeyboardEvent | null>(null);
    const [startPosition, setStartPosition] = useState<number | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [showConfirmDeletionDialog, setShowConfirmDeletionDialog] = useState<boolean>(false);
    const [showConfirmUploadDialog, setShowConfirmUploadDialog] = useState<boolean>(false);

    const [name, setName] = useState<string>('');
    const [createdDate, setCreatedDate] = useState<Date>(new Date(0));
    const [comment, setComment] = useState<string>('');
    const [folderId, setFolderId] = useState<string | null>(null);

    const [folders, setFolders] = useState<Folder[]>([]);

    const open: boolean = Boolean(anchorEl);

    const folder: Folder | null = useMemo<Folder | null>(() => folders.find((currentFolder: Folder) => currentFolder.folderId === folderId) ?? null, [folderId, folders]);
    
    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {

        if (latestKeyEvent !== null && startPosition !== null) {

            (latestKeyEvent.target as HTMLInputElement).selectionStart = startPosition + 1;
            (latestKeyEvent.target as HTMLInputElement).selectionEnd = startPosition + 1;
        }

    }, [comment, latestKeyEvent, startPosition]);

    useEffect(() => { //fetch data from indexedDB

        let mounted: boolean = true;
        
        async function fetchFromIndexedDB(): Promise<void> {

            const commentItem: CommentItem | null = await indexedDBContext.indexedDBService.getCommentItem(props.commentItemId);
            const newFolders: Folder[] = await indexedDBContext.indexedDBService.getAllFolders();

            if (mounted) {
                if (commentItem === null) {
                    return;
                }

                setName(commentItem.name);
                setCreatedDate(commentItem.createdDate);
                setComment(commentItem.comment);
                setFolderId(commentItem.folderId);
                setFolders(newFolders);

                setDataIsLoaded(true);
            }
        }

        void fetchFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService, props.commentItemId]);

    async function handleNameChange(value: string): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        const newChangedDate: Date = new Date();

        const success: boolean = await indexedDBContext.indexedDBService.updateCommentItem(new CommentItem(props.commentItemId, value, createdDate, newChangedDate, comment, folderId)); 
        
        if (!success) {
            setErrorSnackbarMessage('Failed to save name');
            return;
        }
        
        setName(value);
    }

    async function handleCommentChange(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        const newChangedDate: Date = new Date();

        const success: boolean = await indexedDBContext.indexedDBService.updateCommentItem(new CommentItem(props.commentItemId, name, createdDate, newChangedDate, comment, folderId)); 
        
        if (!success) {
            setErrorSnackbarMessage('Failed to save comment');
        }
    }

    async function handleDeleteCommentItem(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        const success: boolean = await indexedDBContext.indexedDBService.removeCommentItem(props.commentItemId);

        if (!success) {
            setErrorSnackbarMessage('Failed to delete comment');
            return;
        }

        props.closeSubTab('Comment deleted');
    }

    async function handleSaveCommentItem(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        //upload the folder first
        if (folder !== null) {

            if (folder.templateId !== null) {
                setErrorSnackbarMessage('Items in folders based on templates can only be uploaded as a whole. Individual items cannot be uploaded');
                return;
            }

            const syncSuccessFolder: boolean = await apiContext.api?.saveFolder(folder.folderId, folder.templateId, folder.name, folder.createdDate, systemContext.serverTimezoneOffsetMinutes ?? 0, tokenContext.token) ?? false;

            if (!syncSuccessFolder) {
                setErrorSnackbarMessage('Failed to upload the comment\'s folder');
                return;
            }
        }

        const syncSuccess: boolean = await apiContext.api?.saveCommentItem(name, createdDate, comment, folderId, systemContext.serverTimezoneOffsetMinutes ?? 0, tokenContext.token) ?? false;

        if (!syncSuccess) {
            setErrorSnackbarMessage('Failed to upload comment');
            return;
        }

        const removeSuccess: boolean = await indexedDBContext.indexedDBService.removeCommentItem(props.commentItemId);

        if (!removeSuccess) {
            setErrorSnackbarMessage('Failed to delete comment (but it has been uploaded)');
            return;
        }

        props.closeSubTab('Comment uploaded');
    }

    return (
        <>
            <AppBar 
                position='sticky'
                elevation={0}
                style={{
                    background: currentTheme.palette.background.paper,
                    height: appBarHeight,
                }}
            >
                <Toolbar style={{
                    justifyContent: 'left',
                }}>
                    <IconButton 
                        edge='end' 
                        color='inherit' 
                        onClick={(): void => props.closeSubTab(null)}
                    >
                        <FontAwesomeIcon
                            icon={faArrowLeft}
                            color={currentTheme.palette.text.primary}    
                        />
                    </IconButton>
                    <Typography
                        noWrap
                        style={{
                            marginLeft: '30px',
                            color: currentTheme.palette.text.primary,
                            
                        }}
                    >
                        {'Comment "' + name + '"'}
                    </Typography>
                    <IconButton
                        onClick={(e: React.MouseEvent<HTMLElement>): void => setAnchorEl(e.currentTarget)}
                        sx={{
                            marginLeft: 'auto',
                            marginRight: '0px',
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faEllipsisVertical}
                            color={currentTheme.palette.text.primary}  
                        />
                    </IconButton>
                </Toolbar>
            </AppBar>
            <div style={{
                textAlign: 'center',
                padding: '25px 5px 5px 5px',
                height: windowSize[1] - navBarHeight - appBarHeight,
                overflow: 'auto',
                position: 'relative',
            }}>
                <QuickTextInput
                    value={name}
                    onBlur={handleNameChange}
                    variant='outlined'
                    dense={true}
                    minWidth={Math.min(windowSize[0] * 0.8, 500)}
                    maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                    labelText='Name'
                    height='40px'
                />
                <br />
                <br />
                <FormControl
                    onClick={(): void => inputRef.current?.focus()}
                    style={{
                        height: '300px',
                        width: Math.min(windowSize[0] * 0.8, 500),
                        overflowX: 'auto',
                        border: '1px solid #777777',
                        borderRadius: '5px',
                        cursor: 'text',
                    }}
                >
                    <Input
                        type='text'
                        value={comment}
                        onChange={(e: React.ChangeEvent): void => setComment((e.target as HTMLInputElement).value)}
                        onKeyDown={(e: React.KeyboardEvent): void => handleTab(e, comment, setComment, setLatestKeyEvent, setStartPosition)}
                        onBlur={async (): Promise<void> => {
                            await handleCommentChange(); //awaits to make sure if finished updating before e.g. switching tab
                        }}
                        multiline={true}
                        disableUnderline={true}
                        inputRef={inputRef}
                    />
                </FormControl>
                <br />
                <br />

                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={(): void => setAnchorEl(null)}
                    onClick={(): void => setAnchorEl(null)}
                    transformOrigin={{
                        horizontal: 'right',
                        vertical: 'top',
                    }}
                    anchorOrigin={{
                        horizontal: 'right',
                        vertical: 'bottom',
                    }}
                >
                    <MenuItem
                        onClick={(): void => setShowConfirmUploadDialog(true)}
                    >
                        Upload
                    </MenuItem>
                    <MenuItem
                        onClick={(): void => setShowConfirmDeletionDialog(true)}
                    >
                        Delete
                    </MenuItem>
                </Menu>

                <ErrorSnackbar
                    message={errorSnackbarMessage}
                    setMessage={setErrorSnackbarMessage}
                />

                <LoadDataBackdrop
                    dataIsLoaded={dataIsLoaded}
                    zIndex={1000}
                />
            </div>

            <ConfirmDialog 
                controller={showConfirmDeletionDialog}
                onClose={async (confirm: boolean): Promise<void> => {
                    setShowConfirmDeletionDialog(false);
                    if (confirm) {
                        await handleDeleteCommentItem();
                    }
                }}
                title='Delete comment'
                text='Are you sure you want to delete this comment?'
                noText='No'
                yesText='Yes'
                loadingConfirm={true}
            />

            <ConfirmDialog 
                controller={showConfirmUploadDialog}
                onClose={async (confirm: boolean): Promise<void> => {
                    setShowConfirmUploadDialog(false);
                    if (confirm) {
                        await handleSaveCommentItem();
                    }
                }}
                title='Upload comment'
                text='Are you sure you want to upload this comment? You can no longer edit it from this app.'
                noText='No'
                yesText='Yes'
                loadingConfirm={true}
            />
        </>
    );
}

export default CommentItemSubTab;